if (!customElements.get('km-blog-listing')) {
  class KMBlogListing extends HTMLElement {
    constructor() {
      super();

      this.resizeFrame = null;
      this.visibleLimit = 6;
      this.activeCategory = 'all';

      this.handleResize = this.handleResize.bind(this);
      this.handleLoadMore = this.handleLoadMore.bind(this);
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.tablist = this.querySelector('[role="tablist"]');
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.indicator = this.querySelector('.km-blog__indicator');
      this.navigationScroll = this.querySelector(
        '.km-blog__navigation-scroll'
      );
      this.articlePanel = this.querySelector(
        '.km-blog__article-panel'
      );
      this.grid = this.querySelector('.km-blog__grid');
      this.cards = this.grid
        ? Array.from(this.grid.querySelectorAll('.km-blog-card'))
        : [];
      this.resultCount = this.querySelector(
        '.km-blog__result-count'
      );
      this.emptyState = this.querySelector('.km-blog__empty');
      this.loadMoreWrap = this.querySelector(
        '.km-blog__load-more-wrap'
      );
      this.loadMoreButton = this.querySelector(
        '.km-blog__load-more'
      );

      const configuredBatchSize = Number.parseInt(
        this.dataset.batchSize,
        10
      );

      if (
        Number.isInteger(configuredBatchSize) &&
        configuredBatchSize > 0
      ) {
        this.visibleLimit = configuredBatchSize;
      }

      this.batchSize = this.visibleLimit;

      if (
        !this.tablist ||
        !this.tabs.length ||
        !this.indicator ||
        !this.grid
      ) {
        return;
      }

      this.dataset.initialized = 'true';

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => {
          this.activateTab(tab, {
            moveFocus: false,
            scrollIntoView: true
          });
        });

        tab.addEventListener('keydown', (event) => {
          this.handleKeydown(event, index);
        });
      });

      if (this.loadMoreButton) {
        this.loadMoreButton.addEventListener(
          'click',
          this.handleLoadMore
        );
      }

      const selectedTab =
        this.tabs.find(
          (tab) => tab.getAttribute('aria-selected') === 'true'
        ) || this.tabs[0];

      this.activateTab(selectedTab, {
        moveFocus: false,
        scrollIntoView: false,
        initial: true
      });

      window.addEventListener('resize', this.handleResize);

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => {
          if (this.isConnected) {
            this.updateIndicator();
          }
        });
      }
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.handleResize);

      if (this.loadMoreButton) {
        this.loadMoreButton.removeEventListener(
          'click',
          this.handleLoadMore
        );
      }

      if (this.resizeFrame) {
        window.cancelAnimationFrame(this.resizeFrame);
      }
    }

    handleKeydown(event, currentIndex) {
      const key = event.key;

      if (
        !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(key)
      ) {
        return;
      }

      event.preventDefault();

      let nextIndex = currentIndex;

      if (key === 'Home') {
        nextIndex = 0;
      } else if (key === 'End') {
        nextIndex = this.tabs.length - 1;
      } else if (key === 'ArrowRight') {
        nextIndex = (currentIndex + 1) % this.tabs.length;
      } else if (key === 'ArrowLeft') {
        nextIndex =
          (currentIndex - 1 + this.tabs.length) %
          this.tabs.length;
      }

      this.activateTab(this.tabs[nextIndex], {
        moveFocus: true,
        scrollIntoView: true
      });
    }

    activateTab(
      tab,
      {
        moveFocus = false,
        scrollIntoView = false,
        initial = false
      } = {}
    ) {
      if (!tab) return;

      const category = tab.dataset.blogCategory || 'all';
      const currentTab = this.tabs.find(
        (item) => item.getAttribute('aria-selected') === 'true'
      );
      const isSameTab = currentTab === tab && !initial;

      if (!isSameTab) {
        this.tabs.forEach((item) => {
          const isActive = item === tab;

          item.setAttribute('aria-selected', String(isActive));
          item.setAttribute('tabindex', isActive ? '0' : '-1');
        });

        this.activeCategory = category;
        this.visibleLimit = this.batchSize;

        if (this.articlePanel) {
          this.articlePanel.setAttribute(
            'aria-labelledby',
            tab.id
          );
        }

        this.updateArticles();

        if (!initial) {
          this.fadeGrid();
        }
      }

      this.updateIndicator(tab);

      if (moveFocus) {
        tab.focus();
      }

      if (scrollIntoView) {
        this.keepTabVisible(tab);
      }
    }

    handleLoadMore() {
      this.visibleLimit += this.batchSize;
      this.updateArticles();
    }

    getMatchingCards() {
      return this.cards.filter((card) => {
        const categories = (
          card.dataset.blogCategories || 'all'
        ).split(/\s+/);

        return categories.includes(this.activeCategory);
      });
    }

    updateArticles() {
      const matchingCards = this.getMatchingCards();
      const matchingSet = new Set(matchingCards);

      this.cards.forEach((card) => {
        const matchingIndex = matchingCards.indexOf(card);
        const shouldShow =
          matchingSet.has(card) &&
          matchingIndex < this.visibleLimit;

        card.hidden = !shouldShow;
      });

      if (this.resultCount) {
        this.resultCount.textContent =
          `共 ${matchingCards.length} 篇文章`;
      }

      if (this.emptyState) {
        this.emptyState.hidden = matchingCards.length !== 0;
      }

      if (this.loadMoreWrap) {
        this.loadMoreWrap.hidden =
          matchingCards.length <= this.visibleLimit;
      }
    }

    fadeGrid() {
      if (
        !this.grid ||
        typeof this.grid.animate !== 'function' ||
        this.prefersReducedMotion()
      ) {
        return;
      }

      this.grid.animate(
        [
          { opacity: 0.35 },
          { opacity: 1 }
        ],
        {
          duration: 190,
          easing: 'ease'
        }
      );
    }

    updateIndicator(activeTab) {
      const tab =
        activeTab ||
        this.tabs.find(
          (item) =>
            item.getAttribute('aria-selected') === 'true'
        );

      if (!tab || !this.indicator) return;

      this.indicator.style.width = `${tab.offsetWidth}px`;
      this.indicator.style.transform =
        `translateX(${tab.offsetLeft}px)`;
    }

    keepTabVisible(tab) {
      if (!this.navigationScroll || !tab) return;

      const hasHorizontalOverflow =
        this.navigationScroll.scrollWidth >
        this.navigationScroll.clientWidth + 1;

      if (!hasHorizontalOverflow) return;

      tab.scrollIntoView({
        behavior: this.prefersReducedMotion()
          ? 'auto'
          : 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }

    prefersReducedMotion() {
      return window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;
    }

    handleResize() {
      if (this.resizeFrame) {
        window.cancelAnimationFrame(this.resizeFrame);
      }

      this.resizeFrame = window.requestAnimationFrame(() => {
        this.updateIndicator();
        this.resizeFrame = null;
      });
    }
  }

  customElements.define('km-blog-listing', KMBlogListing);
}